import { Button, Card, Spin, Table, Tabs, Tag } from "antd";
import { AntPageContainer } from "../../shared/components/AntPageContainer";
import { useEffect, useState } from "react";
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import surveyApi, {
  SurveyStats as SurveyStatsType,
  SurveyDetail,
  QuestionType,
  QUESTION_TYPE_LABELS,
  MultilingualText,
} from "../../survey/api";
import useAlert from "../../shared/hooks/useAlert";

const COLORS = [
  "#1E88E5",
  "#43A047",
  "#FB8C00",
  "#8E24AA",
  "#00ACC1",
  "#F4511E",
  "#6D4C41",
  "#E53935",
  "#5E35B1",
  "#00897B",
];

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  fr: "Français",
  ko: "한국인",
  vi: "Tiếng Việt",
  zh: "中文",
  th: "แบบไทย",
  es: "Español",
  ru: "Русский",
  hi: "हिन्दी",
};

const getLocalizedTitle = (
  title: MultilingualText | undefined,
  fallback: string = "Untitled"
): string => {
  if (!title) return fallback;
  return (
    title.en ||
    title.fr ||
    title.ko ||
    title.vi ||
    title.zh ||
    title.th ||
    title.es ||
    title.ru ||
    title.hi ||
    fallback
  );
};

export const SurveyStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [stats, setStats] = useState<SurveyStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedRatings, setExpandedRatings] = useState<Set<number>>(
    new Set()
  );
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
    undefined
  );

  const toggleRatingExpanded = (questionId: number) => {
    setExpandedRatings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Fetch survey details first
  useEffect(() => {
    if (id) {
      fetchSurvey(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch stats when survey is loaded or channel changes
  useEffect(() => {
    if (id && survey) {
      fetchStats(parseInt(id), selectedChannel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, survey, selectedChannel]);

  const fetchSurvey = async (surveyId: number) => {
    try {
      const response = await surveyApi.getById(surveyId);
      setSurvey(response.data ?? null);
    } catch (error) {
      console.error("Error fetching survey:", error);
      setAlert("Error loading survey", "error");
      navigate("/admin/surveys");
    }
  };

  const fetchStats = async (surveyId: number, channel?: string) => {
    try {
      setLoading(true);
      const response = await surveyApi.getStats(surveyId, channel);
      setStats(response.data ?? null);
    } catch (error) {
      console.error("Error fetching stats:", error);
      setAlert("Error loading survey statistics", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle channel tab change
  const handleChannelChange = (channel: string) => {
    setSelectedChannel(channel === "all" ? undefined : channel);
  };


  // Transform language data for chart
  const languageData = stats
    ? Object.entries(stats.byLanguage).map(([code, count]) => ({
        name: LANGUAGE_LABELS[code] || code,
        count,
      }))
    : [];

  // Transform daily submissions for chart
  const dailySubmissions = stats
    ? Object.entries(stats.dailySubmissions)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-14) // Last 14 days
    : [];

  if (loading) {
    return (
      <AntPageContainer>
        <div className="w-full h-[70vh] flex justify-center items-center">
          <Spin size="large" />
        </div>
      </AntPageContainer>
    );
  }

  if (!stats) {
    return (
      <AntPageContainer>
        <div className="text-center py-8">
          <p>Statistics not available</p>
          <Button onClick={() => navigate("/admin/surveys")}>Go Back</Button>
        </div>
      </AntPageContainer>
    );
  }

  return (
    <AntPageContainer>
      <div className="w-full min-h-screen">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/admin/surveys")}
            >
              Back
            </Button>
            <h1 className="text-2xl font-bold m-0">
              {getLocalizedTitle(stats.surveyTitle)} - Statistics
            </h1>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchStats(stats.surveyId, selectedChannel)}
          >
            Refresh
          </Button>
        </div>

        {/* Channel Filter Tabs */}
        {survey?.channels && survey.channels.length > 0 && (
          <Tabs
            activeKey={selectedChannel || "all"}
            onChange={handleChannelChange}
            className="mb-4"
            items={[
              {
                key: "all",
                label: "All Channels",
              },
              ...survey.channels.map((ch) => ({
                key: ch,
                label: ch,
              })),
            ]}
          />
        )}

        {/* Summary Card */}
        <Card className="mb-6">
          <div className="flex justify-center items-center gap-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">
                {stats.totalResponses}
              </div>
              <div className="text-gray-500">Total Responses</div>
            </div>
          </div>
        </Card>

        {/* Question Statistics - Show choice and rating questions */}
        <Card title="Question-by-Question Analysis" className="mb-6">
          {stats.questionStats
            .filter(
              (qStat) =>
                qStat.type === QuestionType.MULTIPLE_CHOICE ||
                qStat.type === QuestionType.MULTI_SELECT ||
                qStat.type === QuestionType.RATING
            )
            .map((qStat, index) => (
              <div
                key={qStat.questionId}
                className="mb-8 pb-8 border-b last:border-b-0 last:mb-0 last:pb-0"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-blue-100 shrink-0 text-blue-800 font-bold rounded-full w-8 min-w-8 h-8 min-h-8 flex items-center justify-center">
                    {index + 1}
                  </span>
                  <h3 className="text-lg font-semibold m-0">
                    {getLocalizedTitle(qStat.questionTitle)}
                  </h3>
                  <Tag color="blue">{QUESTION_TYPE_LABELS[qStat.type]}</Tag>
                </div>

                {/* Multiple Choice / Multi Select Stats */}
                {qStat.optionCounts && (
                  <div className="ml-11">
                    <Table
                      dataSource={(() => {
                        const totalSelections = Object.values(
                          qStat.optionCounts
                        ).reduce((sum, count) => sum + count, 0);
                        return Object.entries(qStat.optionCounts).map(
                          ([optionText, count]) => ({
                            optionText,
                            count,
                            percentage:
                              totalSelections > 0
                                ? ((count / totalSelections) * 100).toFixed(1)
                                : 0,
                          })
                        );
                      })()}
                      rowKey="optionText"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: "Option",
                          dataIndex: "optionText",
                          width: 300,
                        },
                        {
                          title: "Percentage",
                          dataIndex: "percentage",
                          render: (val: string, record: { count: number }) => (
                            <div className="flex items-center gap-2">
                              <div className="w-[200px] bg-gray-200 rounded h-2">
                                <div
                                  className="h-2 bg-blue-500 rounded"
                                  style={{ width: `${val}%` }}
                                />
                              </div>
                              <span>
                                {val}% ({record.count})
                              </span>
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                )}

                {/* Rating Stats */}
                {qStat.type === QuestionType.RATING && (
                  <div className="ml-11">
                    {qStat.averageRating !== undefined && (
                      <div
                        className="mb-4 p-4 bg-yellow-50 rounded-lg inline-block cursor-pointer hover:bg-yellow-100 transition-colors"
                        onClick={() => toggleRatingExpanded(qStat.questionId)}
                        title="Click to show/hide rating distribution"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-3xl font-bold text-yellow-600">
                            {qStat.averageRating}
                          </span>
                          <div className="flex items-center">
                            {[...Array(10)].map((_, i) => (
                              <span
                                key={i}
                                className={`text-xl ${
                                  i < Math.round(qStat.averageRating || 0)
                                    ? "text-yellow-400"
                                    : "text-gray-300"
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-gray-500">/ 10 average</span>
                          <span className="text-gray-400 text-sm ml-2">
                            {expandedRatings.has(qStat.questionId) ? "▲" : "▼"}
                          </span>
                        </div>
                      </div>
                    )}
                    {qStat.ratingDistribution &&
                      expandedRatings.has(qStat.questionId) && (
                        <Table
                          dataSource={(() => {
                            const totalRatings = Object.values(
                              qStat.ratingDistribution
                            ).reduce((sum, count) => sum + count, 0);
                            return Object.entries(qStat.ratingDistribution)
                              .map(([rating, count]) => ({
                                rating: parseInt(rating),
                                count,
                                percentage:
                                  totalRatings > 0
                                    ? ((count / totalRatings) * 100).toFixed(1)
                                    : "0",
                              }))
                              .sort((a, b) => b.rating - a.rating);
                          })()}
                          rowKey="rating"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: "Rating",
                              dataIndex: "rating",
                              width: 120,
                              render: (val: number) => (
                                <div className="flex items-center gap-1">
                                  <span className="font-medium w-6">{val}</span>
                                  <span className="text-yellow-400">
                                    {"★".repeat(val)}
                                  </span>
                                </div>
                              ),
                            },
                            {
                              title: "Distribution",
                              dataIndex: "percentage",
                              render: (
                                val: string,
                                record: { count: number }
                              ) => (
                                <div className="flex items-center gap-2">
                                  <div className="w-[200px] bg-gray-200 rounded h-2">
                                    <div
                                      className="h-2 bg-yellow-400 rounded"
                                      style={{ width: `${val}%` }}
                                    />
                                  </div>
                                  <span>
                                    {val}% ({record.count})
                                  </span>
                                </div>
                              ),
                            },
                          ]}
                        />
                      )}
                  </div>
                )}
              </div>
            ))}

          {stats.questionStats.filter(
            (qStat) =>
              qStat.type === QuestionType.MULTIPLE_CHOICE ||
              qStat.type === QuestionType.MULTI_SELECT ||
              qStat.type === QuestionType.RATING
          ).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No choice or rating questions in this survey.
            </div>
          )}
        </Card>
        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Language Distribution */}
          <Card title="Responses by Language">
            {languageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    dataKey="count"
                    isAnimationActive={true}
                    data={languageData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {languageData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </Card>

          {/* Daily Submissions */}
          <Card title="Daily Submissions (Last 14 Days)">
            {dailySubmissions.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailySubmissions}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                      })
                    }
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    }
                  />
                  <Bar dataKey="count" fill="#1E88E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </Card>
        </div>
      </div>
    </AntPageContainer>
  );
};
